/**
 * Діалог «Запросити користувача» (Порція 3.0.5.4 — повна форма).
 *
 * Поля:
 *   - email — кому надсилаємо.
 *   - tenant_role — MEMBER (з pre-grant user_access) або ADMIN
 *     (повний доступ до tenant'а, pre-grant ігнорується backend'ом).
 *   - initial_user_access[] (тільки для MEMBER) — для кожної org tenant'а
 *     можна вибрати access_role (PROJECT_ADMIN / CHIEF_ACCOUNTANT /
 *     FINANCIAL_ANALYST / AUDITOR / OBSERVER / VIEWER / ...) АБО
 *     «Без доступу» (не створюємо запис).
 *
 * Після успішного створення → показ invitation_url з кнопкою «Скопіювати».
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  createInvitation,
  type InitialUserAccess,
  type InvitationCreated,
  type TenantRole,
} from '@/api/invitations';
import { orgs } from '@/api/catalogs';
import { accessRoles, type AccessRole } from '@/api/admin';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function InviteUserDialog({ onClose, onCreated }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('MEMBER');
  /** organization_id → access_role_id або '' (= без доступу). */
  const [access, setAccess] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<InvitationCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Завантажуємо доступні організації tenant'а.
  const orgsQuery = useQuery({
    queryKey: ['orgs-list'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  // Завантажуємо доступні access roles.
  const rolesQuery = useQuery({
    queryKey: ['access-roles'],
    queryFn: () => accessRoles.list(),
  });

  const mut = useMutation({
    mutationFn: () => {
      const grants: InitialUserAccess[] =
        role === 'MEMBER'
          ? Object.entries(access)
              .filter(([, roleId]) => roleId !== '')
              .map(([organization_id, access_role_id]) => ({
                organization_id,
                access_role_id,
              }))
          : [];
      return createInvitation({
        email: email.trim(),
        tenant_role: role,
        initial_user_access: grants,
      });
    },
    onSuccess: (data) => setCreated(data),
    onError: (e) => {
      const err = e as AxiosError<{ detail?: string }>;
      setLocalError(err.response?.data?.detail ?? 'Не вдалося створити запрошення');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!email.trim()) {
      setLocalError('Введіть email');
      return;
    }
    mut.mutate();
  }

  async function copyToClipboard(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Скопіюйте посилання:', url);
    }
  }

  function handleClose() {
    if (created) onCreated();
    else onClose();
  }

  const orgsList = orgsQuery.data?.items ?? [];
  const rolesList = rolesQuery.data ?? [];

  return (
    <>
      <div className="backdrop" onClick={handleClose} />
      <div className="drawer" style={{ width: 'min(640px, 100vw)' }}>
        <h2>Запросити користувача</h2>

        {!created && (
          <form onSubmit={onSubmit}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                autoComplete="off"
                required
                autoFocus
              />
            </div>
            <div className="field">
              <label>Роль у tenant'і</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as TenantRole)}
              >
                <option value="MEMBER">Учасник — доступи налаштовуються нижче</option>
                <option value="ADMIN">Адміністратор — повний доступ до всіх юросіб</option>
              </select>
              <small style={{ color: 'var(--text-secondary)' }}>
                OWNER через UI не призначається — створіть власний tenant
                через signup.
              </small>
            </div>

            {role === 'MEMBER' && (
              <div className="field">
                <label>Доступи до юросіб (опційно)</label>
                {orgsQuery.isLoading && <div>Завантаження…</div>}
                {!orgsQuery.isLoading && orgsList.length === 0 && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    У вас ще немає юросіб. Створіть їх у /organizations,
                    після цього можна буде вибирати доступ.
                  </div>
                )}
                {orgsList.length > 0 && (
                  <table style={{ width: '100%', marginTop: '0.5rem' }}>
                    <tbody>
                      {orgsList.map((o) => (
                        <tr key={o.id}>
                          <td
                            style={{ padding: '0.3rem 0.5rem 0.3rem 0', width: '50%' }}
                          >
                            {o.name}
                          </td>
                          <td style={{ padding: '0.3rem 0' }}>
                            <select
                              value={access[o.id] ?? ''}
                              onChange={(e) =>
                                setAccess((prev) => ({
                                  ...prev,
                                  [o.id]: e.target.value,
                                }))
                              }
                            >
                              <option value="">— Без доступу —</option>
                              {rolesList.map((r: AccessRole) => (
                                <option key={r.id} value={r.id}>
                                  {r.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <small style={{ color: 'var(--text-secondary)' }}>
                  Можна не вибирати зараз — налаштувати після прийняття
                  запрошення через Admin → Доступи.
                </small>
              </div>
            )}

            {localError && <div className="error">{localError}</div>}

            <div className="drawer__actions">
              <button type="button" onClick={onClose}>
                Скасувати
              </button>
              <button
                type="submit"
                className="primary"
                disabled={mut.isPending}
              >
                {mut.isPending ? 'Створення…' : 'Створити запрошення'}
              </button>
            </div>
          </form>
        )}

        {created && (
          <div>
            <div
              style={{
                background: 'var(--success-soft)',
                color: 'var(--success)',
                padding: '0.6rem 0.8rem',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              ✓ Запрошення створено для {created.email}
            </div>

            {created.invitation_url && (
              <>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  Надішліть це посилання запрошеному (діє 7 днів):
                </p>
                <div
                  style={{
                    background: 'var(--bg-soft)',
                    padding: '0.6rem',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    wordBreak: 'break-all',
                    marginBottom: '0.5rem',
                  }}
                >
                  {created.invitation_url}
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(created.invitation_url!)}
                >
                  {copied ? '✓ Скопійовано' : 'Скопіювати посилання'}
                </button>
              </>
            )}

            <div className="drawer__actions">
              <button type="button" className="primary" onClick={handleClose}>
                Готово
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
